import { useState, useEffect, useMemo } from 'react';

import { useQuery } from '@zenbusiness/application-commons-graphql';
import { useNavigate } from 'react-router-dom';

import { PAGINATION_DEFAULT_LIMIT } from '#client/constants/pagination'; // Update as needed
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import { GET_LENDING_PROSPECTS } from '#client/queries';

export default function useLendingProspects() {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();

  const [page, setPage] = useState(1); // Defaults to page 1
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigate = useNavigate();

  const updateData = (prevData, data) => {
    const lendingProspects = isLoadingMore
      ? [...prevData.lendingProspects.data, ...data.lendingProspects.data]
      : data.lendingProspects.data;

    setIsLoadingMore(false);

    return {
      lendingProspects: {
        data: lendingProspects,
        pageInfo: data.lendingProspects.pageInfo
      }
    };
  };

  const {
    error,
    errors,
    loading: loadingProspects,
    data: originalData,
    refetch
  } = useQuery(GET_LENDING_PROSPECTS, {
    variables: {
      filter: {
        businessEntityUuid
      },
      pagination: {
        page,
        pageSize: PAGINATION_DEFAULT_LIMIT
      }
    },
    skip: !businessEntityUuid,
    updateData,
    noThrow: true,
    skipCache: true
  });

  useLogError(errors, error);
  const data = useMemoObject(originalData);

  const prospects = useMemo(
    () => data?.lendingProspects?.data ?? [],
    [data?.lendingProspects?.data]
  );

  const hasMore = useMemo(
    () => !!data?.lendingProspects?.pageInfo?.hasNextPage && prospects.length > 0,
    [prospects.length, data?.lendingProspects?.pageInfo?.hasNextPage]
  );

  const loadMore = () => {
    if (hasMore) {
      setIsLoadingMore(true);
      setPage((prevPage) => prevPage + 1); // Move to the next page
    }
  };

  const resetPagination = () => {
    if (page > 1) {
      setPage(1); // Reset to the first page
    } else {
      refetch(); // Force refetch if already on the first page
    }
  };

  useEffect(() => {
    if (error) {
      navigate('', { replace: true });
      createCommonErrorToast({ error });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, navigate]);

  return {
    data,
    prospects,
    error,
    loadingProspects,
    isLoadingMore,
    hasMore,
    loadMore,
    refetch,
    resetPagination
  };
}
